<template>
  <div>
    <DialogComponent
      id="edit-modal"
      class="form-modal"
      v-loading="modalLoading"
      :visible.sync="visible"
      :fullscreen="true"
      :show-close="false"
      :modal="false"
      @open="populateFields"
    >
      <div class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">{{ context }} Location</h1>
          <div class="modal-actions col-2">
            <el-button
              v-if="positionComputed && !positionChanged && addressFieldVerified"
              type="primary"
              @click="handleSubmit"
            >
              {{ context === "Edit" ? "Save" : "Create" }}
            </el-button>
            <el-button type="default" @click="handleClose"> Cancel</el-button>
          </div>
        </div>
      </div>

      <el-form>
        <recent-audits service="E911 Location" :record="currentRecord" />

        <el-tabs v-model="activeTab">
          <el-tab-pane label="General" name="general">
            <el-input v-model="form.name" placeholder="Enter name">
              <template slot="prefix">Name</template>
            </el-input>
            <el-input
              :disabled="addressFieldVerified"
              name="autoAddress"
              v-model="address"
              placeholder="Search for address"
            >
              <template slot="prefix">Search address</template>
            </el-input>
            <div class="address-fields" v-if="addressFieldsActive">
              <div class="flex w-full justify-space-between">
                <h2 class="form-section-header" style="margin-left: 0; margin-right: 0;">Address</h2>
                <el-button
                  v-loading="validatePending"
                  @click="addressFieldVerified ? resetAddressValidation() : validateAddressLocation()"
                  type="primary"
                >
                  {{  addressFieldVerified ? "Edit" : "Validate" }}
                </el-button>
              </div>
              <div class="inline-form-group">
                <el-col :span="8">
                  <el-input
                    :disabled="addressFieldVerified"
                    v-model="form.streetNum"
                    placeholder="Enter street number"
                  >
                    <template slot="prefix">Street number</template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="15">
                  <el-input
                    :disabled="addressFieldVerified"
                    v-model="form.streetInfo"
                    placeholder="Enter street name"
                  >
                    <template slot="prefix">Street Name</template>
                  </el-input>
                </el-col>
              </div>
              <div class="inline-form-group">
                <el-col :span="8">
                  <el-select
                    :disabled="addressFieldVerified"
                    v-model="form.locationType"
                    :value="form.locationType"
                    placeholder="Select location type"
                    clearable
                  >
                    <template slot="prefix">Building / suite</template>
                    <el-option
                      v-for="location in locationOptions"
                      :key="location"
                      :label="location"
                      :value="location"
                    >
                    </el-option>
                  </el-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="15">
                  <el-input
                    :disabled="addressFieldVerified"
                    v-model="form.locationNumber"
                    placeholder="Enter building / suite number"
                  >
                    <template slot="prefix">Building / suite number</template>
                  </el-input>
                </el-col>
              </div>
              <div class="inline-form-group">
                <el-col :span="11">
                  <el-input :disabled="addressFieldVerified" v-model="form.city" placeholder="Enter city">
                    <template slot="prefix">City</template>
                  </el-input>
                </el-col>
                <el-col :span="1"></el-col>
                <el-col :span="5">
                  <el-input :disabled="addressFieldVerified" v-model="form.state" placeholder="Enter state">
                    <template slot="prefix">State</template>
                  </el-input>
                </el-col>
                <el-col :span="1"></el-col>
                <el-col :span="6">
                  <el-input :disabled="addressFieldVerified" v-model="form.postalCode" placeholder="Enter zip">
                    <template slot="prefix">Zip code</template>
                  </el-input>
                </el-col>
              </div>
              <div class="flex w-full justify-space-between">
                <h2 class="form-section-header" style="margin-left: 0; margin-right: 0;">GPS Coordinates</h2>
                <el-button
                  @click="positionComputed && !positionChanged ? positionChanged = true : validateAddress()"
                  type="primary"
                >
                  {{ positionComputed && !positionChanged ? "Edit" : "Validate" }}
                </el-button>
              </div>
              <div class="inline-form-group">
                <el-col :span="8">
                  <el-input
                    :disabled="!positionChanged"
                    v-model.number="position.lat"
                    placeholder="Enter latitude"
                  >
                    <template slot="prefix">Latitude</template>
                  </el-input>
                </el-col>
                <el-col :span="1" />
                <el-col :span="8">
                  <el-input
                    :disabled="!positionChanged"
                    v-model.number="position.lng"
                    placeholder="Enter longitude"
                  >
                    <template slot="prefix">Longitude</template>
                  </el-input>
                </el-col>
              </div>
            </div>

            <div v-if="positionComputed && !positionChanged">
              <div class="inline-form-group">
                <el-checkbox v-model="verified">Location Verified</el-checkbox>
              </div>
              <div class="inline-form-group">
                <GMap
                  :options="mapOptions"
                  :center="position"
                  :zoom="15"
                  map-type-id="terrain"
                  style="width: 100%; height: 450px; margin-top: 15px"
                >
                  <GMarker :position="position" />
                </GMap>
              </div>
            </div>
          </el-tab-pane>

          <el-tab-pane label="Notification Group" name="notification_groups">
            <el-tag class="mb-10" type="warning" v-show="!form.notificationGroups.some(item => item.id)">
              <i class="el-icon-warning" style="margin-right: 4px;" />
              Notifies all branch extensions if none selected
            </el-tag>
            <div
              class="inline-form-group"
              v-for="(item, i) in form.notificationGroups"
              :key="i + '$(^^)$'"
            >
              <el-col :span="16">
                <dads-select
                  placeholder="Select extension"
                  :remote-method="(query) => remoteSearchExtensions(query, i)"
                  v-model="item.id"
                  remote
                  filterable
                  clearable
                  @change="($event) => onNotificationGroupSelection($event, i)"
                >
                  <template slot="prefix">Extension</template>
                  <el-option
                    v-for="(el, j) in item.options"
                    :key="j"
                    :label="el.name + ' - ' + el.number"
                    :value="el.id"
                  />
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="2" class="text-right">
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Remove extension"
                  placement="top"
                >
                  <el-button
                    class="ml-5"
                    style="float: right; margin-right: 1px"
                    icon="el-icon-delete"
                    @click="removeNotificationGroup(i)"
                  />
                </el-tooltip>
              </el-col>
            </div>

            <el-button
              type="text"
              icon="el-icon-circle-plus-outline"
              :disabled="disabledToAddNotificationGroup"
              @click="addNotificationGroup"
            >
              Add extension
            </el-button>
          </el-tab-pane>
        </el-tabs>
      </el-form>

      <el-dialog
        :visible.sync="addressCandidatesVisible"
        :close-on-press-escape="true"
        :show-close="true"
        :close-on-click-modal="false"
        :modal="true"
        width="40%"
        style="min-width: 300px;"
      >
        <el-form>
          <h2 class="form-section-header">Verify Address</h2>
          <h3 class="form-section-header">We couldn't verify the address you entered, but we found a list of verified alternatives.</h3>
          <el-select
            v-model="addressCandidate"
            :value="addressCandidate"
            placeholder="Select valid location"
            clearable
          >
            <template slot="prefix">Address Candidate</template>
            <el-option
              v-for="(address, index) in addressCandidates"
              :key="index"
              :label="formatAddressCandidate(address)"
              :value="index"
            >
            </el-option>
          </el-select>
        </el-form>
        <div class="inline-form-group justify-flex-end">
          <el-button @click="populateValidatedAddressFields(addressCandidate)"> Confirm </el-button>
          <el-button @click="addressCandidatesVisible = false"> Cancel </el-button>
        </div>
      </el-dialog>
    </DialogComponent>
  </div>
</template>

<script>
import { debounce } from "lodash";
import DadsSelect from "@/components/select";
import RecentAudits from "@/components/RecentAudits";
import GMap from "vue2-google-maps/src/components/map.vue";
import GMarker from "vue2-google-maps/src/components/marker";
import feathersClient, { apiV2Host } from "@/feathers/feathers-client";
import DialogComponent from "../../../../components/CustomModalComponent";

export default {
  name: "locationsForm",
  components: {
    DialogComponent,
    RecentAudits,
    GMap,
    GMarker,
    DadsSelect,
  },

  props: {
    context: { required: true },
    recordId: { default: "" },
    visible: { default: false },
  },

  data() {
    return {
      activeTab: "general",
      modalLoading: false,
      address: "",
      locationOptions: ["BLDG", "FL", "STE", "APT"],
      addressFieldsActive: false,
      currentRecord: null,
      addressOptionsVisible: false,
      validAddressOptions: [],
      validatePending: false,

      form: {
        name: "",
        locationType: "",
        locationNumber: "",
        streetNum: "",
        streetInfo: "",
        city: "",
        state: "",
        postalCode: "",
        notificationGroups: [
          {
            id: null,
            options: [],
            value: null,
          },
        ],
      },
      positionChanged: false,
      position: {
        lat: "",
        lng: "",
      },
      data: null,
      verified: false,

      mapOptions: {
        zoomControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
        draggable: true,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        disableDefaultUI: false,
      },
      addressCandidatesVisible: false,
      addressCandidate: null,
      addressCandidates: [],
      addressFieldVerified: false
    };
  },

  computed: {
    disabledToAddNotificationGroup() {
      if (this.form.notificationGroups.length) {
        const lastIdx = this.form.notificationGroups.length - 1;
        return !this.form.notificationGroups[lastIdx].id;
      }
    },

    modalVisible() {
      return this.visible;
    },

    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    positionComputed() {
      return this.position.lat && this.position.lng;
    },

    positionPopulated() {
      if (!this.currentRecord || this.context === "Create") {
        return false;
      }

      const { lat, lng } = this.position;
      return `${lat},${lng}` === this.currentRecord.address.loc;
    },
  },

  watch: {
    modalVisible() {
      if (this.context === "Edit") {
        this.addressFieldsActive = true;
      }
    },

    currentRecord(record) {
      if (record && record.address.loc) {
        const [lat, lng] = record.address.loc.split(",").map((n) => Number(n));
        this.position = { lat, lng };
      }
    },

    position: {
      deep: true,
      handler(pos) {
        if (!pos.lat && !pos.lng) return;

        this.positionChanged = !this.positionPopulated;
      },
    },
  },

  methods: {
    addNotificationGroup() {
      this.form.notificationGroups.push({
        options: [],
        id: null,
        value: null,
      });
    },

    removeNotificationGroup(idx) {
      this.form.notificationGroups.splice(idx, 1);

      if (idx === 0) {
        this.addNotificationGroup();
      }
    },

    onNotificationGroupSelection(extId, idx) {
      if (!extId) {
        this.form.notificationGroups[idx].value = null;
        return;
      }

      const ext = this.form.notificationGroups[idx].options.find(
        (ng) => ng.id === extId
      );

      if (ext) {
        this.form.notificationGroups[idx].value = {
          id: extId,
          name: ext.name,
          number: ext.number,
          email: ext.email,
          peerName: ext.peerName
        };
      }
    },

    async remoteSearchExtensions(query, idx) {
      const search = debounce(async () => {
        if (query && query.length) {
          const searchTerm = (query + "")
            .replace(/[\\"']/g, "\\$&")
            .replace(/\u0000/g, "\\0");

          let queryTerm = {
            tenantId: this.selectedTenant.id,
            $limit: 25,
            $or: [
              { number: { $like: `%${searchTerm}%` } },
              { name: { $like: `%${searchTerm}%` } },
            ],
          };

          const exts = await feathersClient
            .service("extensions")
            .find({ query: queryTerm });

          this.form.notificationGroups[idx].options = [...exts];
        }
      }, 500);

      search();
    },

    handleSubmit() {
      if (!this.verified || !this.addressFieldVerified) {
        this.activeTab = "general";
        this.$notify({
          title: "Error",
          message: "Please validate your address and / or location!",
          type: "error",
        });

        return;
      }

      if (!this.positionPopulated && !this.verified) {
        this.activeTab = "general";
        this.$notify({
          title: "Error",
          message: "Please verify the location!",
          type: "error",
        });

        return;
      }

      if (this.data) {
        this.data.name = this.form.name;
      }

      if (this.context === "Create") {
        this.createTheLocation();
      }

      if (this.context === "Edit") {
        this.editTheLocation();
      }
    },

    async validateAddress() {
      // USING ONLY GOOGLE PLACES //
      if (!this.position.lat || !this.position.lng) {
        this.$notify({
          title: "Form error",
          message: "Please fill out the latitude and longitude fields.",
          type: "error",
        });
      }

      if (!this.positionComputed) {
        return;
      }

      this.positionChanged = false;

      this.verified = false;

      this.$notify({
        title: "Map Generated!",
        message: "Please verify the location.",
        type: "warning",
      });

      this.activeTab = "general";

      // this.setAddressDetails();
    },

    setAddressDetails() {
      const { lat, lng } = this.position;

      const address = {
        streetNum: this.form.streetNum,
        streetInfo: this.form.streetInfo,
        location: `${this.form.locationType} ${this.form.locationNumber}`,
        city: this.form.city,
        state: this.form.state,
        postalCode: this.form.postalCode,
        loc: `${lat},${lng}`,
      };

      const uniqueGroups = new Set(
        this.form.notificationGroups.filter((ng) => !!ng.id).map((ng) => ng.id)
      );

      const notificationGroups = [];
      uniqueGroups.forEach((groupId) => {
        const matchedGroup = this.form.notificationGroups.find(
          (g) => groupId === g.id
        );

        if (matchedGroup) {
          notificationGroups.push(matchedGroup.value);
        }
      });

      this.data = {
        tenantId: this.selectedTenant.id,
        name: this.form.name,
        address,
        notificationGroups,
      };
    },

    createTheLocation() {
      this.setAddressDetails();

      this.$confirm("Confirm E911 creation?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(async () => {
          this.modalLoading = true;
          try {
            const res = await fetch(`${apiV2Host}/admin/emergency-locations`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                Accept: "application/json",
              },
              body: JSON.stringify(this.data),
            });

            const response = await res.json();
            this.modalLoading = false;

            if (!res.ok) {
              const { name, message, validationErrors } = response;
              this.$notify({
                title: name,
                message:
                  validationErrors && validationErrors.length
                    ? validationErrors[0]
                    : message,
                type: "error",
              });
              return;
            }

            this.$notify({
              title: "Success",
              message: "Location " + response.name + " has been created!",
              type: "success",
            });

            this.$emit("modalClosed", response);
            this.clearFormFields();
          } catch (err) {
            this.modalLoading = false;
          }
        })
        .catch(() => {
          this.modalLoading = false;
        });
    },

    async patchLocation() {
      return fetch(
        `${apiV2Host}/admin/emergency-locations/${this.currentRecord.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json",
          },
          body: JSON.stringify(this.data),
        }
      );
    },

    editTheLocation(data) {
      this.setAddressDetails();

      this.$confirm("Confirm E911 edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(async () => {
          try {
            this.modalLoading = true;
            const res = await this.patchLocation();
            const response = await res.json();

            this.modalLoading = false;

            if (!res.ok) {
              const { name, message, validationErrors } = response;
              this.$notify({
                title: name,
                message:
                  validationErrors && validationErrors.length
                    ? validationErrors[0]
                    : message,
                type: "error",
              });
              return;
            }

            this.$notify({
              title: "Success",
              message: "E911 " + response.name + " has been updated!",
              type: "success",
            });
            this.$emit("modalClosed", response);
            this.clearFormFields();
          } catch (err) {
            this.modalLoading = false;
          }
        })
        .catch(() => true);
    },

    handleClose() {
      this.$emit("modalClosed");
      this.clearFormFields();
    },

    clearFormFields() {
      Object.keys(this.form).forEach((key) => {
        this.form[key] = typeof this.form[key] === typeof [] ? [] : "";
      });
      this.form.notificationGroups = [
        {
          id: null,
          options: [],
          value: null,
        },
      ];
      this.address = "";
      this.addressFieldsActive = false;
      this.validAddressOptions = [];
      this.positionChanged = false;
      this.position = { lat: "", lng: "" };
      this.verified = false;
      this.data = null;
      this.activeTab = "general";
      this.addressCandidatesVisible = false;
      this.addressCandidate = null;
      this.addressCandidates = [];
      this.addressFieldVerified = false;
    },

    populateAddressFields(obj) {
      this.form.streetNum = obj["street_number"];
      this.form.streetInfo = obj["route"];
      this.form.city = obj["locality"];
      this.form.state = obj["administrative_area_level_1"];
      this.form.postalCode = obj["postal_code"];
      this.form.locationType = "";
      this.form.locationNumber = "";
      this.address =
        obj["street_number"] +
        " " +
        obj["route"] +
        " " +
        obj["locality"] +
        ", " +
        obj["administrative_area_level_1"] +
        " " +
        obj["postal_code"];
    },

    async populateFields() {
      if (this.context === "Create") {
        this.addressFieldsActive = false;
        this.addressFieldVerified = false;
      }

      if (this.recordId) {
        fetch(`${apiV2Host}/admin/emergency-locations/${this.recordId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json",
          },
        })
          .then((res) => res.json())
          .then((record) => {
            this.currentRecord = record;

            const { name, address, notificationGroups } = record;

            this.form.name = name;
            this.form.streetNum = address.streetNum;
            this.form.streetInfo = address.streetInfo;
            this.form.city = address.city;
            this.form.state = address.state;
            this.form.postalCode = address.postalCode;
            if (address.location.length) {
              let location_extras = address.location.split(" ");
              this.form.locationNumber = location_extras[1];
              this.form.locationType = location_extras[0];
            }

            if (notificationGroups && notificationGroups.length) {
              this.form.notificationGroups = notificationGroups.map((ng) => ({
                id: ng.id,
                options: [ng],
                value: ng,
              }));
            }

            this.data = {
              name: this.form.name,
              tenantId: this.selectedTenant.id,
              address: {
                ...address,
              },
              notificationGroups: notificationGroups || [],
            };

            this.addressFieldVerified = true;
            this.verified = true;
          })
          .catch((err) => {});
      }
    },

    async validateAddressLocation() {
      this.validatePending = true;
      const data = {
        city: this.form.city,
        state: this.form.state,
        location: `${this.form.locationType} ${this.form.locationNumber}`,
        streetNum: this.form.streetNum,
        postalCode: this.form.postalCode,
        streetInfo: this.form.streetInfo
      };

      fetch(`${apiV2Host}/admin/emergency-locations/validate-address`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then(errData => {
              throw errData;
            });
          }
          return res.json();
        })
        .then((record) => {
          // We found a match from inteliquent
          if (record && record.type === "corrected") {
            this.populateValidatedAddressFields(record.addressList[0]);
            this.addressFieldVerified = true;
          }

          // We did not find a match from inteliquent,
          // but we have a list of verified addresses similar to the one entered
          if (record && record.type === "candidates") {
            this.addressFieldVerified = false;
            this.addressCandidatesVisible = true;
            this.addressCandidates = record.addressList;
            this.addressCandidate = 0;
          }
          this.validatePending = false;
        })
        .catch((err) => {
          this.validatePending = false;
          if (err.code === 404) {
            this.$notify({
              title: "Error",
              message: "We could not find a match for the address that you entered. Check you address or please contact support.",
              type: "error",
            });
          } else {
            this.$notify({
              title: "Error",
              message: err.message,
              type: "error",
            });
          }
        });
    },

    populateValidatedAddressFields(addressIndex) {

      //get the address using the index
      const address = this.addressCandidates[addressIndex];
      if (!address) return;

      this.form.city = address.city;
      this.form.state = address.state;
      this.form.streetNum = address.streetNum;
      this.form.postalCode = address.postalCode;
      this.form.streetInfo = address.streetInfo;

      if (address.location && address.location.length) {
        let location_extras = address.location.split(" ");
        this.form.locationNumber = location_extras[1];
        this.form.locationType = location_extras[0];
      }

      if (this.addressCandidatesVisible) {
        this.addressCandidatesVisible = false;
      }

      this.addressFieldVerified = true;
    },

    formatAddressCandidate(address) {
      return `${address.streetNum} ${address.streetInfo}, ${address.city}, ${address.state} ${address.postalCode}`;
    },

    resetAddressValidation() {
      this.addressFieldVerified = false;
    }
  },

  mounted() {
    if (this.context === "Create") {
      this.addressFieldsActive = false;
      this.addressFieldVerified = false;
    }

    let locationInfo = {
      street_number: "short_name",
      route: "long_name",
      locality: "long_name",
      administrative_area_level_1: "short_name",
      postal_code: "short_name",
    };

    this.autocomplete = new google.maps.places.Autocomplete(
      document.querySelector("input[name='autoAddress']")
    );
    this.autocomplete.setFields(["address_components", "geometry"]);
    this.autocomplete.setComponentRestrictions({
      country: ["us", "vi", "ca"],
    });
    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace();
      const ac = place.address_components;

      this.position.lat = place.geometry.location.lat();
      this.position.lng = place.geometry.location.lng();

      const locationObject = {
        street_number: "",
        route: "",
        locality: "",
        administrative_area_level_1: "",
        postal_code: "",
      };

      for (let i = 0; i < ac.length; i++) {
        let addressType = ac[i].types[0];
        if (locationInfo[addressType]) {
          locationObject[addressType] = ac[i][locationInfo[addressType]];
        }
      }

      //here we will update our address object sent with the request
      this.populateAddressFields(locationObject);
      this.addressFieldsActive = true;
    });
  },
};
</script>

<style lang="scss" scoped>
.current-address-details {
  max-width: 600px;
  margin: 10px auto;
}

.el-tabs {
  max-width: 600px;
  margin: auto;
}

.el-checkbox {
  margin-left: 0;
}
</style>
